// One issue's workspace: what the reader wants, who is involved, why it matters, what is still
// open, what needs checking and the suggested next step.
//
// Mor writes these in the same pass as its replies in the reader's own conversation about the
// issue, and a card under the reply names what changed. The reader can also edit any text here
// directly and dismiss a question, a checking item or a person. Every change,
// Mor's or the reader's, is a new version; nothing here is replaced in place, and History lists
// them all.
//
// A section is drawn only when it has something in it. An entry written under the newest card
// or divider in the conversation is labelled "New" when it is the first version of that entry,
// "Corrected" when it replaces a reading the reader said was wrong and "Updated" otherwise; the
// label is the word, never a colour.
//
// What the reader may see of an issue is decided by the server: anyone who was not in the
// conversation a version was written in sees a bare title.

import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { ApiFailure, type ApiClient } from '../api/client.js';
import type {
  DecisionNotes,
  IssueBasis,
  IssueChecking,
  IssueInvolvement,
  IssuePosition,
  IssueQuestion,
  FileView,
  IssueView,
  NoteView,
  Person,
  RecordChange,
  TopicView,
} from '../api/contract.js';
import { ENVIRONMENT } from '../build.js';
import { AddFile } from '../components/AddFile.js';
import { FileLine } from '../components/FileRows.js';
import {
  FormButtons,
  RemoveConfirm,
  RowMenu,
  cancelOnEscape,
  focusRowMenu,
} from '../components/RowEdit.js';
import { principalsRoomLabel, relationLabel } from '../format.js';
import { basisWord, newestTurnId, positionWord, turnLabel } from '../plan/issue.js';
import { principalsThreadOf, viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import type { IssueState } from '../plan/useIssue.js';

export type IssuePageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
  /** The topic the address names. */
  topic: TopicView;
  issueId: string;
  issue: IssueState;
  /** The issue's own conversation, which an added file records as its origin. */
  originThreadId: string | null;
};

/** How many of a long list show before "Show all". */
const PREVIEW = 2;

/** Whether this build shows the notes tagged with an issue: local and the development site only. */
const SHOWS_DEBUG = ENVIRONMENT === null || ENVIRONMENT === 'development';

const POSITIONS: readonly IssuePosition[] = ['decided', 'tentative', 'undecided'];

/** Which entry is open for editing or confirming, if any. */
type Open = { readonly kind: 'edit' | 'dismiss'; readonly key: string } | null;

export function IssuePage({
  state,
  client,
  planId,
  topic,
  issueId,
  issue,
  originThreadId,
}: IssuePageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  // Set by an added-issue card's Explore issue: the conversation to return to, one step.
  const arrivedFrom = (useLocation().state as { back?: unknown } | null)?.back;
  const back =
    arrivedFrom !== null &&
    typeof arrivedFrom === 'object' &&
    typeof (arrivedFrom as { to?: unknown }).to === 'string' &&
    typeof (arrivedFrom as { label?: unknown }).label === 'string'
      ? (arrivedFrom as { to: string; label: string })
      : null;
  const [menuFor, setMenuFor] = useState<string | null>(null);
  const [open, setOpen] = useState<Open>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [allQuestions, setAllQuestions] = useState(false);
  const [allChecking, setAllChecking] = useState(false);

  const [files, setFiles] = useState<FileView[]>([]);
  const [filesVersion, setFilesVersion] = useState(0);
  const reloadFiles = useCallback(() => setFilesVersion((value) => value + 1), []);
  useEffect(() => {
    let current = true;
    client.listFiles(planId).then(
      (loaded) => {
        if (current) {
          setFiles(loaded);
        }
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [client, planId, snapshot, filesVersion]);

  const topicPath = `/plans/${planId}/topics/${topic.key}/issues`;
  const issuePath = `${topicPath}/${issueId}`;

  if (snapshot === null) {
    return null;
  }
  if (issue.status === 'missing') {
    return (
      <section className="page">
        <p className="screen__problem">This isn’t available to you.</p>
      </section>
    );
  }
  const view = issue.view;
  if (view === null) {
    return issue.status === 'faltered' ? (
      <section className="page">
        <p className="screen__problem">That didn’t load. Try again.</p>
      </section>
    ) : null;
  }

  if (view.removed) {
    return (
      <section className="page issue">
        <h2 className="page__title">{view.title}</h2>
        <p className="note">This issue was removed from the list.</p>
      </section>
    );
  }

  const newest = newestTurnId(snapshot.messages);
  const canEdit = viewerIsPrincipal(snapshot) && topic.viewer.invited;

  /** Sends one change. Resolves true when it was accepted. */
  const send = async (change: RecordChange): Promise<boolean> => {
    setBusy(true);
    setProblem(null);
    try {
      await client.changeRecord(planId, change);
      issue.refetch();
      state.refetch();
      return true;
    } catch (error) {
      setProblem(
        error instanceof ApiFailure && error.code === 'refused'
          ? error.message
          : 'That didn’t save. Try again.',
      );
      return false;
    } finally {
      setBusy(false);
    }
  };

  const close = (): void => {
    setProblem(null);
    setOpen(null);
  };
  const run = async (change: RecordChange): Promise<void> => {
    if (await send(change)) {
      setOpen(null);
    }
  };
  const isOpen = (kind: 'edit' | 'dismiss', key: string): boolean =>
    open !== null && open.kind === kind && open.key === key;
  const mark = (entry: {
    seq: number;
    turnMessageId: string;
    correction: boolean;
  }): JSX.Element | null => {
    const label = turnLabel(entry, newest);
    return label === null ? null : <span className="issueRow__mark">{label}</span>;
  };
  const basisMark = (basis: IssueBasis): JSX.Element | null => {
    const word = basisWord(basis);
    return word === null ? null : <span className="issueRow__basis">{word}</span>;
  };
  const menu = (
    key: string,
    name: string,
    dismissible: boolean,
  ): JSX.Element | undefined =>
    canEdit ? (
      <RowMenu
        open={menuFor === key}
        onToggle={() => setMenuFor(menuFor === key ? null : key)}
        items={[
          {
            label: 'Edit',
            run: () => {
              setProblem(null);
              setOpen({ kind: 'edit', key });
            },
          },
          ...(dismissible
            ? [
                {
                  label: 'Dismiss',
                  run: () => {
                    setProblem(null);
                    setOpen({ kind: 'dismiss', key });
                  },
                },
              ]
            : []),
        ]}
        onPick={() => setMenuFor(null)}
        name={name}
        rowId={key}
      />
    ) : undefined;
  const dismissConfirm = (key: string, question: string, change: RecordChange) =>
    isOpen('dismiss', key) ? (
      <RemoveConfirm
        question={question}
        actionLabel="Dismiss"
        busy={busy}
        error={problem}
        onCancel={() => {
          close();
          focusRowMenu(key);
        }}
        onRemove={() => void run(change)}
      />
    ) : null;
  // ------------------------------------------------------------------ what you want
  const intention = view.intention;
  const wantRow =
    intention === null ? null : (
      <ul className="people__list">
        <li className="person issueRow">
          <span className="person__identity">
            {isOpen('edit', 'intention') ? (
              <IntentionForm
                initialText={intention.text}
                initialPosition={intention.position}
                busy={busy}
                error={problem}
                onCancel={close}
                onSave={(text, position) =>
                  run({ kind: 'set_issue_intention', issue: view.id, text, position })
                }
              />
            ) : (
              <>
                <span className="issueRow__text">
                  {intention.text}
                  {mark(intention)}
                </span>
                {intention.position === null ? null : (
                  <span className="person__detail">
                    {positionWord(intention.position)}
                  </span>
                )}
              </>
            )}
          </span>
          {isOpen('edit', 'intention')
            ? undefined
            : menu('intention', 'what you want', false)}
        </li>
      </ul>
    );

  // ------------------------------------------------------------------ who is involved
  const involvementRow = (row: IssueInvolvement): JSX.Element => {
    const known = snapshot.people.find((p) => p.id === row.person.id);
    const relation =
      known !== undefined && known.relation !== null
        ? relationLabel(known.relation)
        : row.person.relationship;
    const key = `involvement:${row.id}`;
    return (
      <li key={row.id} className="personLine issueRow">
        <span className="personLine__text">
          {isOpen('edit', key) ? (
            <InvolvementFields
              people={snapshot.people}
              fixedPerson={row.person.name}
              initialPerson={row.person.id}
              initialText={row.text}
              busy={busy}
              error={problem}
              onCancel={close}
              onSave={(person, text) =>
                run({
                  kind: 'set_issue_involvement',
                  issue: view.id,
                  person,
                  text,
                })
              }
            />
          ) : (
            <>
              <span className="issueRow__text">
                <span className="personLine__name">{row.person.name}</span>
                {relation === null ? null : (
                  <span className="personLine__detail"> · {relation}</span>
                )}
                {row.text === '' ? null : (
                  <span className="personLine__detail"> · {row.text}</span>
                )}
                {mark(row)}
              </span>
            </>
          )}
          {dismissConfirm(key, `Dismiss ${row.person.name} from who is involved?`, {
            kind: 'dismiss_issue_involvement',
            issue: view.id,
            involvement: row.id,
          })}
        </span>
        {isOpen('edit', key) ? undefined : menu(key, row.person.name, true)}
      </li>
    );
  };

  // ------------------------------------------------------------------ what's still open
  const questionRow = (row: IssueQuestion): JSX.Element => {
    const key = `question:${row.id}`;
    return (
      <li key={row.id} className="person issueRow">
        <span className="person__identity">
          {isOpen('edit', key) ? (
            <QuestionForm
              initialQuestion={row.question}
              initialView={row.viewSoFar}
              busy={busy}
              error={problem}
              onCancel={close}
              onSave={(text, viewSoFar) =>
                run({
                  kind: 'set_issue_question',
                  issue: view.id,
                  question: row.id,
                  text,
                  viewSoFar,
                })
              }
            />
          ) : (
            <>
              <span className="issueRow__text">
                {row.question}
                {mark(row)}
              </span>
              {row.viewSoFar === '' ? null : (
                <span className="person__detail">View so far: {row.viewSoFar}</span>
              )}
            </>
          )}
          {dismissConfirm(key, 'Dismiss this question?', {
            kind: 'dismiss_issue_question',
            issue: view.id,
            question: row.id,
          })}
        </span>
        {isOpen('edit', key) ? undefined : menu(key, row.question, true)}
      </li>
    );
  };

  // ------------------------------------------------------------------ what needs checking
  const checkingRow = (row: IssueChecking): JSX.Element => {
    const key = `checking:${row.id}`;
    return (
      <li key={row.id} className="person issueRow">
        <span className="person__identity">
          {isOpen('edit', key) ? (
            <CheckingForm
              people={snapshot.people}
              initial={row}
              busy={busy}
              error={problem}
              onCancel={close}
              onSave={(text, knownSoFar, answerer, professionalReview, basis) =>
                run({
                  kind: 'set_issue_checking',
                  issue: view.id,
                  item: row.id,
                  text,
                  knownSoFar,
                  answerer,
                  professionalReview,
                  basis,
                })
              }
            />
          ) : (
            <>
              <span className="issueRow__text">
                {row.text}
                {mark(row)}
                {basisMark(row.basis)}
              </span>
              {row.knownSoFar === '' ? null : (
                <span className="person__detail">Known so far: {row.knownSoFar}</span>
              )}
              {row.answerer === null ? null : (
                <span className="person__detail">Could answer: {row.answerer.name}</span>
              )}
              {row.professionalReview ? (
                <span className="person__detail">Needs professional review</span>
              ) : null}
            </>
          )}
          {dismissConfirm(key, 'Dismiss this item?', {
            kind: 'dismiss_issue_checking',
            issue: view.id,
            item: row.id,
          })}
        </span>
        {isOpen('edit', key) ? undefined : menu(key, row.text, true)}
      </li>
    );
  };

  const unsettled = view.checking.filter((row) => !row.settled);
  const settled = view.checking.filter((row) => row.settled);
  const questionsShown = allQuestions ? view.questions : view.questions.slice(0, PREVIEW);
  const checkingShown = allChecking ? unsettled : unsettled.slice(0, PREVIEW);

  const hasInvolvement = view.involvement.length > 0;
  const hasQuestions = view.questions.length > 0;
  const hasChecking = view.checking.length > 0;
  const hasNextStep = view.nextStep !== null && view.nextStep.text !== '';
  const hasArrangements = view.arrangements !== null && view.arrangements.text !== '';
  const empty =
    intention === null &&
    !hasInvolvement &&
    view.rationale === null &&
    !hasQuestions &&
    !hasChecking &&
    !hasNextStep &&
    !hasArrangements;
  return (
    <section className="page issue">
      {back === null ? null : (
        <Link className="issue__back" to={back.to}>
          ← Back to {back.label}
        </Link>
      )}
      <div className="issue__head">
        <h2 className="page__title">{view.title}</h2>
        <span className="issue__links">
          <Link to={`${issuePath}/standing`}>Where things stand</Link>
          <Link to={`${issuePath}/history`}>History</Link>
        </span>
      </div>
      {wantRow === null ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">What you want</h3>
          {wantRow}
        </section>
      )}
      {hasInvolvement ? (
        <section className="issue__section">
          <h3 className="sectionLabel">Who is involved</h3>
          <ul className="people__list people__list--lines">
            {view.involvement.map(involvementRow)}
          </ul>
        </section>
      ) : null}
      {view.rationale === null ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">Why it matters</h3>
          <ul className="people__list">
            <li className="person issueRow">
              <span className="person__identity">
                {isOpen('edit', 'rationale') ? (
                  <TextEntryForm
                    label="Why it matters"
                    initial={view.rationale.text}
                    allowEmpty={false}
                    busy={busy}
                    error={problem}
                    onCancel={close}
                    onSave={(text) =>
                      run({ kind: 'set_issue_rationale', issue: view.id, text })
                    }
                  />
                ) : (
                  <span className="issueRow__text">
                    {view.rationale.text}
                    {mark(view.rationale)}
                  </span>
                )}
              </span>
              {isOpen('edit', 'rationale')
                ? undefined
                : menu('rationale', 'why it matters', false)}
            </li>
          </ul>
        </section>
      )}
      <section className="issue__section">
        <h3 className="sectionLabel">Existing arrangements</h3>
        {view.arrangements === null || view.arrangements.text === '' ? null : (
          <ul className="people__list">
            <li className="person issueRow">
              <span className="person__identity">
                {isOpen('edit', 'arrangements') ? (
                  <TextEntryForm
                    label="Existing arrangements"
                    initial={view.arrangements.text}
                    allowEmpty
                    busy={busy}
                    error={problem}
                    onCancel={close}
                    onSave={(text) =>
                      run({ kind: 'set_issue_arrangements', issue: view.id, text })
                    }
                  />
                ) : (
                  <span className="issueRow__text">
                    {view.arrangements.text}
                    {mark(view.arrangements)}
                  </span>
                )}
              </span>
              {isOpen('edit', 'arrangements')
                ? undefined
                : menu('arrangements', 'existing arrangements', false)}
            </li>
          </ul>
        )}
        {files.length === 0 ? null : (
          <ul className="fileLines">
            {files.map((file) => (
              <FileLine
                key={file.id}
                client={client}
                planId={planId}
                file={file}
                people={snapshot.people}
                viewerIsPrincipal={viewerIsPrincipal(snapshot)}
                onChanged={reloadFiles}
              />
            ))}
          </ul>
        )}
        <AddFile
          client={client}
          planId={planId}
          people={snapshot.people}
          viewerIsPrincipal={viewerIsPrincipal(snapshot)}
          originThreadId={originThreadId}
          asLink
          onAdded={reloadFiles}
        />
      </section>
      {hasQuestions ? (
        <section className="issue__section">
          <h3 className="sectionLabel">What’s still open ({view.questions.length})</h3>
          <ul className="people__list">{questionsShown.map(questionRow)}</ul>
          {view.questions.length > PREVIEW ? (
            <button
              type="button"
              className="discoveryLink"
              onClick={() => setAllQuestions(!allQuestions)}
            >
              {allQuestions ? 'Show fewer' : `Show all ${view.questions.length}`}
            </button>
          ) : null}
        </section>
      ) : null}
      {hasChecking ? (
        <section className="issue__section">
          <h3 className="sectionLabel">What needs checking ({unsettled.length})</h3>
          <ul className="people__list">{checkingShown.map(checkingRow)}</ul>
          {unsettled.length > PREVIEW ? (
            <button
              type="button"
              className="discoveryLink"
              onClick={() => setAllChecking(!allChecking)}
            >
              {allChecking ? 'Show fewer' : `Show all ${unsettled.length}`}
            </button>
          ) : null}
          {settled.length === 0 ? null : (
            <details className="issue__settled">
              <summary>Settled ({settled.length})</summary>
              <ul className="people__list">{settled.map(checkingRow)}</ul>
            </details>
          )}
        </section>
      ) : null}
      {view.nextStep === null || view.nextStep.text === '' ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">Suggested next step</h3>
          <ul className="people__list">
            <li className="person issueRow">
              <span className="person__identity">
                {isOpen('edit', 'next_step') ? (
                  <TextEntryForm
                    label="Suggested next step"
                    initial={view.nextStep.text}
                    allowEmpty
                    busy={busy}
                    error={problem}
                    onCancel={close}
                    onSave={(text) =>
                      run({ kind: 'set_issue_next_step', issue: view.id, text })
                    }
                  />
                ) : (
                  <span className="issueRow__text">
                    {view.nextStep.text}
                    {mark(view.nextStep)}
                  </span>
                )}
              </span>
              {isOpen('edit', 'next_step')
                ? undefined
                : menu('next_step', 'the suggested next step', false)}
            </li>
          </ul>
        </section>
      )}
      {empty ? (
        <p className="note">This page is a record of your conversation with Mor.</p>
      ) : null}
      {problem === null || open !== null ? null : (
        <p className="page__problem" role="alert">
          {problem}
        </p>
      )}
      {SHOWS_DEBUG ? (
        <IssueDebugNotes
          principalsLabel={principalsRoomLabel(
            principalsThreadOf(snapshot.threads, topic.key),
            snapshot.people,
          )}
          client={client}
          planId={planId}
          issueId={issueId}
          refreshOn={snapshot}
        />
      ) : null}
    </section>
  );
}

// ------------------------------------------------------------------------- forms

/** The form every entry shares: its fields, a refusal line, Save and Cancel. */
function EntryForm({
  children,
  busy,
  error,
  canSave,
  onSubmit,
  onCancel,
}: {
  children: ReactNode;
  busy: boolean;
  error: string | null;
  canSave: boolean;
  onSubmit: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <form
      className="issueForm"
      noValidate
      onKeyDown={cancelOnEscape(onCancel)}
      onSubmit={(event) => {
        event.preventDefault();
        if (canSave && !busy) {
          onSubmit();
        }
      }}
    >
      {children}
      {error === null ? null : <p className="inlineForm__error">{error}</p>}
      <FormButtons submitLabel="Save" disabled={busy || !canSave} onCancel={onCancel} />
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  autoFocus = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
}): JSX.Element {
  return (
    <label className="issueForm__field">
      <span className="issueForm__label">{label}</span>
      <textarea
        className="inlineForm__input"
        rows={2}
        value={value}
        autoFocus={autoFocus}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function TextEntryForm({
  label,
  initial,
  allowEmpty,
  busy,
  error,
  onCancel,
  onSave,
}: {
  label: string;
  initial: string;
  allowEmpty: boolean;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (text: string) => Promise<void>;
}): JSX.Element {
  const [text, setText] = useState(initial);
  return (
    <EntryForm
      busy={busy}
      error={error}
      canSave={allowEmpty || text.trim() !== ''}
      onCancel={onCancel}
      onSubmit={() => void onSave(text.trim())}
    >
      <Field label={label} value={text} onChange={setText} autoFocus />
    </EntryForm>
  );
}

function IntentionForm({
  initialText,
  initialPosition,
  busy,
  error,
  onCancel,
  onSave,
}: {
  initialText: string;
  initialPosition: IssuePosition | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (text: string, position: IssuePosition | null) => Promise<void>;
}): JSX.Element {
  const group = useId();
  const [text, setText] = useState(initialText);
  const [position, setPosition] = useState<IssuePosition | null>(initialPosition);
  return (
    <EntryForm
      busy={busy}
      error={error}
      canSave={text.trim() !== ''}
      onCancel={onCancel}
      onSubmit={() => void onSave(text.trim(), position)}
    >
      <Field label="What you want" value={text} onChange={setText} autoFocus />
      <fieldset className="fileChoice">
        <legend className="fileChoice__legend">How settled is it?</legend>
        {[...POSITIONS, null].map((option) => (
          <label key={option ?? 'unset'} className="fileChoice__option">
            <input
              type="radio"
              className="radio"
              name={group}
              checked={position === option}
              onChange={() => setPosition(option)}
            />
            <span className="fileChoice__text">
              <span className="fileChoice__label">
                {option === null ? 'Not set' : positionWord(option)}
              </span>
            </span>
          </label>
        ))}
      </fieldset>
    </EntryForm>
  );
}

function InvolvementFields({
  people,
  fixedPerson,
  initialPerson,
  initialText,
  busy,
  error,
  onCancel,
  onSave,
}: {
  people: readonly Person[];
  /** The person's name when editing a row, whose person cannot change. */
  fixedPerson: string | null;
  initialPerson: string;
  initialText: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (person: string, text: string) => Promise<void>;
}): JSX.Element {
  const [person, setPerson] = useState(initialPerson);
  const [text, setText] = useState(initialText);
  const ready = person !== '' && text.trim() !== '';
  return (
    <form
      className="issueForm"
      noValidate
      onKeyDown={cancelOnEscape(onCancel)}
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !busy) {
          void onSave(person, text.trim());
        }
      }}
    >
      {fixedPerson === null ? (
        <label className="issueForm__field">
          <span className="issueForm__label">Person</span>
          <select
            className="inlineForm__input"
            value={person}
            onChange={(event) => setPerson(event.target.value)}
          >
            <option value="">Choose a person</option>
            {people.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
              </option>
            ))}
          </select>
        </label>
      ) : (
        <p className="person__name">{fixedPerson}</p>
      )}
      <Field label="Their part in this issue" value={text} onChange={setText} autoFocus />
      {error === null ? null : <p className="inlineForm__error">{error}</p>}
      <FormButtons submitLabel="Save" disabled={busy || !ready} onCancel={onCancel} />
    </form>
  );
}

const BASES: readonly { value: IssueBasis; label: string }[] = [
  { value: 'statement', label: 'Unconfirmed' },
  { value: 'expectation', label: 'Assumed' },
  { value: 'unknown', label: 'Not known' },
];

function BasisChoice({
  value,
  onChange,
}: {
  value: IssueBasis;
  onChange: (value: IssueBasis) => void;
}): JSX.Element {
  const group = useId();
  return (
    <fieldset className="fileChoice">
      <legend className="fileChoice__legend">What it rests on</legend>
      {BASES.map((option) => (
        <label key={option.value} className="fileChoice__option">
          <input
            type="radio"
            className="radio"
            name={group}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <span className="fileChoice__text">
            <span className="fileChoice__label">{option.label}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}

function QuestionForm({
  initialQuestion,
  initialView,
  busy,
  error,
  onCancel,
  onSave,
}: {
  initialQuestion: string;
  initialView: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (question: string, viewSoFar: string) => Promise<void>;
}): JSX.Element {
  const [question, setQuestion] = useState(initialQuestion);
  const [viewSoFar, setViewSoFar] = useState(initialView);
  return (
    <EntryForm
      busy={busy}
      error={error}
      canSave={question.trim() !== ''}
      onCancel={onCancel}
      onSubmit={() => void onSave(question.trim(), viewSoFar.trim())}
    >
      <Field label="Question" value={question} onChange={setQuestion} autoFocus />
      <Field label="View so far" value={viewSoFar} onChange={setViewSoFar} />
    </EntryForm>
  );
}

function CheckingForm({
  people,
  initial,
  busy,
  error,
  onCancel,
  onSave,
}: {
  people: readonly Person[];
  initial: IssueChecking | null;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (
    text: string,
    knownSoFar: string,
    answerer: string | null,
    professionalReview: boolean,
    basis: IssueBasis,
  ) => Promise<void>;
}): JSX.Element {
  const [text, setText] = useState(initial?.text ?? '');
  const [known, setKnown] = useState(initial?.knownSoFar ?? '');
  const [answerer, setAnswerer] = useState(initial?.answerer?.id ?? '');
  const [review, setReview] = useState(initial?.professionalReview ?? false);
  const [basis, setBasis] = useState<IssueBasis>(initial?.basis ?? 'statement');
  return (
    <EntryForm
      busy={busy}
      error={error}
      canSave={text.trim() !== ''}
      onCancel={onCancel}
      onSubmit={() =>
        void onSave(
          text.trim(),
          known.trim(),
          answerer === '' ? null : answerer,
          review,
          basis,
        )
      }
    >
      <Field label="What needs checking" value={text} onChange={setText} autoFocus />
      <Field label="Known so far" value={known} onChange={setKnown} />
      <BasisChoice value={basis} onChange={setBasis} />
      <label className="issueForm__field">
        <span className="issueForm__label">Who could answer it</span>
        <select
          className="inlineForm__input"
          value={answerer}
          onChange={(event) => setAnswerer(event.target.value)}
        >
          <option value="">No one in particular</option>
          {people.map((candidate) => (
            <option key={candidate.id} value={candidate.id}>
              {candidate.name}
            </option>
          ))}
        </select>
      </label>
      <label className="issueForm__check">
        <input
          type="checkbox"
          className="checkbox"
          checked={review}
          onChange={(event) => setReview(event.target.checked)}
        />
        <span>Needs professional review</span>
      </label>
    </EntryForm>
  );
}

// -------------------------------------------------------------------- debug notes

/** "March 4", with the year added when it is not this one. Always a date, never a time. */
function noteDate(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) {
    return '';
  }
  const sameYear = at.getFullYear() === new Date().getFullYear();
  return at.toLocaleDateString(undefined, {
    month: 'long',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

/**
 * The notes tagged with this issue, shown on local and the development site only. The server
 * decides what the reader may see of them; this shows what came back.
 */
function IssueDebugNotes({
  client,
  planId,
  issueId,
  refreshOn,
  principalsLabel,
}: {
  client: ApiClient;
  planId: string;
  issueId: string;
  refreshOn: unknown;
  principalsLabel: string;
}): JSX.Element | null {
  const [notes, setNotes] = useState<DecisionNotes | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let current = true;
    client.decisionNotes(planId, issueId).then(
      (loaded) => {
        if (current) {
          setNotes(loaded);
        }
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [client, planId, issueId, refreshOn]);

  if (notes === null) {
    return null;
  }
  return (
    <section className="issue__section issue__debug">
      <details open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
        <summary className="sectionLabel issue__debugSummary">Debug</summary>
        {open ? (
          <>
            <p className="sectionLabel">Notes about this issue</p>
            <NoteList
              title="What has been said"
              items={notes.shared}
              own={false}
              planId={planId}
              principalsLabel={principalsLabel}
            />
            <NoteList
              title="What you’ve told Mor privately"
              items={notes.own}
              own
              planId={planId}
              principalsLabel={principalsLabel}
            />
            {notes.shared.length === 0 && notes.own.length === 0 ? (
              <p className="decisionNotes__empty">No notes about this yet.</p>
            ) : null}
          </>
        ) : null}
      </details>
    </section>
  );
}

function NoteList({
  title,
  items,
  own,
  planId,
  principalsLabel,
}: {
  title: string;
  items: readonly NoteView[];
  own: boolean;
  planId: string;
  principalsLabel: string;
}): JSX.Element | null {
  if (items.length === 0) {
    return null;
  }
  return (
    <div className="decisionNotes__section">
      <h4 className="decisionNotes__title">{title}</h4>
      <div
        className={
          own ? 'decisionNotes__list decisionNotes__list--own' : 'decisionNotes__list'
        }
      >
        {items.map((note, index) => (
          <article className="decisionNote" key={index}>
            <p className="decisionNote__text">{note.text}</p>
            <NoteSource note={note} planId={planId} principalsLabel={principalsLabel} />
          </article>
        ))}
      </div>
    </div>
  );
}

function NoteSource({
  note,
  planId,
  principalsLabel,
}: {
  note: NoteView;
  planId: string;
  principalsLabel: string;
}): JSX.Element {
  const source = note.source;
  const date = noteDate(note.at);
  if (source.kind === 'file') {
    return (
      <p className="decisionNote__source">
        From the file{' '}
        <Link to={`/plans/${planId}/files/${source.fileId}`}>{source.name}</Link>
        {date === '' ? null : ` · ${date}`}
      </p>
    );
  }
  const label =
    source.thread === 'shared'
      ? 'Everyone and Mor'
      : source.thread === 'principals'
        ? `You, ${principalsLabel} and Mor`
        : 'You and Mor';
  const room =
    note.speaker === null
      ? label
      : `${note.speaker.you ? 'You' : note.speaker.name} · ${label}`;
  return (
    <p className="decisionNote__source">{date === '' ? room : `${room} · ${date}`}</p>
  );
}
